import React, { useState } from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import { Button } from "./ui/button";
import { apiClient } from "../lib/api";
import { useTheme, THEME_META, YOUTH_THEMES, ADULT_THEMES } from "../context/ThemeContext";
import { useT } from "../context/LanguageContext";
import { Star, Music2 } from "lucide-react";

/**
 * One-time first-login modal that lets a new user preview and pick a theme.
 * Opens automatically when `user.theme_onboarded === false`. Clicking any swatch
 * applies the theme live (whole app reskins behind the modal); the "Use this look"
 * button persists the choice and calls PUT /me/theme-onboarded so it never opens again.
 */
export default function ThemeOnboardingModal({ open, onClose }) {
  const t = useT();
  const { theme, setTheme } = useTheme();
  const [picked, setPicked] = useState(theme);

  const apply = (id) => { setPicked(id); setTheme(id); };

  const confirm = async () => {
    try { await apiClient.put("/me/theme-onboarded"); } catch { /* non-fatal */ }
    onClose();
  };

  const skip = async () => {
    try { await apiClient.put("/me/theme-onboarded"); } catch { /* non-fatal */ }
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) skip(); }}>
      <DialogContent className="max-w-2xl" data-testid="theme-onboarding-modal">
        <div className="text-center pt-2">
          <h2 className="font-display text-3xl font-bold mb-1">{t("Pick your look")}</h2>
          <p className="text-sm" style={{ color: "var(--app-text-muted)" }}>
            {t("Try them all — the whole app reskins as you tap. You can change this anytime.")}
          </p>
        </div>

        {/* Live preview tile — reskins as the user toggles themes */}
        <div className="my-5 rounded-2xl border-2 overflow-hidden" data-testid="theme-preview-tile"
             style={{ borderColor: "var(--app-border)", background: "var(--app-surface)" }}>
          <div className="px-5 py-4" style={{ background: "var(--app-primary)", color: "#fff" }}>
            <div className="text-[11px] uppercase tracking-widest font-bold opacity-80">{t("Practice today")}</div>
            <div className="font-display text-2xl font-bold leading-tight mt-0.5">{t("Hello, Avery")}</div>
          </div>
          <div className="grid grid-cols-3 gap-3 p-5">
            <div className="text-center">
              <Star className="w-6 h-6 mx-auto mb-1" style={{ color: "var(--app-accent)" }} />
              <div className="font-display text-2xl font-bold" style={{ color: "var(--app-text)" }}>14</div>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--app-text-muted)" }}>{t("day streak")}</div>
            </div>
            <div className="text-center">
              <Music2 className="w-6 h-6 mx-auto mb-1" style={{ color: "var(--app-primary)" }} />
              <div className="font-display text-2xl font-bold" style={{ color: "var(--app-text)" }}>7/12</div>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--app-text-muted)" }}>{t("scales")}</div>
            </div>
            <div className="text-center">
              <Star className="w-6 h-6 mx-auto mb-1" style={{ color: "var(--app-accent)" }} />
              <div className="font-display text-2xl font-bold" style={{ color: "var(--app-text)" }}>23</div>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--app-text-muted)" }}>{t("songs")}</div>
            </div>
          </div>
        </div>

        {/* Theme swatches — two groups */}
        <div className="space-y-4">
          <ThemeGroup title={t("Youth")} ids={YOUTH_THEMES} picked={picked} onPick={apply} t={t} />
          <ThemeGroup title={t("Adult")} ids={ADULT_THEMES} picked={picked} onPick={apply} t={t} />
        </div>

        <div className="flex items-center justify-end gap-2 mt-6">
          <Button variant="ghost" onClick={skip} data-testid="theme-onboard-skip">{t("Skip for now")}</Button>
          <Button onClick={confirm} className="btn-pill btn-primary h-11" data-testid="theme-onboard-confirm">
            {t("Use this look")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ThemeGroup({ title, ids, picked, onPick, t }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: "var(--app-text-muted)" }}>
        {title}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {ids.map((id) => {
          const meta = THEME_META[id];
          const active = picked === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onPick(id)}
              data-testid={`onboard-theme-${id}`}
              className={`rounded-xl p-2 border-2 transition-all text-left ${active ? "ring-2" : "hover:-translate-y-0.5"}`}
              style={{
                borderColor: active ? "var(--app-accent)" : "var(--app-border)",
                background: active ? "var(--app-surface-2)" : "var(--app-surface)",
              }}
            >
              <span className="block w-full h-8 rounded-md mb-1.5" style={{ background: meta.swatch }} />
              <div className="text-xs font-semibold" style={{ color: "var(--app-text)" }}>
                {active ? "✓ " : ""}{t(meta.label)}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
